import {
    AsyncOperationId,
    AsyncOperationStatus,
    gmailMailboxUnavailableCode,
    InternalId,
    isAsyncOperationTerminal,
    outlookTemporarilyUnavailableCode,
} from "@sp-crm/core";
import { AsyncOperationProgress } from "components/async-operation/async-operation-progress";
import {
    standardAsyncOperationPollIntervalMs,
    useAsyncOperation,
} from "components/async-operation/use-async-operation";
import { useRefreshFeedWhenSendSettles } from "components/async-operation/use-refresh-feed-when-send-settles";
import {
    allRejectedBody,
    allRejectedRetryPrompt,
    allRejectedTitle,
    authErrorBody,
    authErrorTitle,
    describeDeliveryOutcome,
    gmailMailboxUnavailableBody,
    gmailMailboxUnavailableTitle,
    mailboxUnavailableBody,
    mailboxUnavailableTitle,
    outlookTemporarilyUnavailableRecipientsBody,
    outlookTemporarilyUnavailableTitle,
    partialSendBody,
    partialSendTitle,
    unknownErrorBody,
    unknownErrorTitle,
} from "components/messages/delivery-outcome";
import { trpcClient } from "components/rpc/trpc";
import { fancyAlert } from "components/ui/fancy-alert";
import { fancyChoice } from "components/ui/fancy-choice";
import { fancyConfirm } from "components/ui/fancy-confirm";
import { PrimaryButton } from "components/ui/primary-button";
import { SecondaryButton } from "components/ui/secondary-button";
import { Spinner } from "components/ui/spinner";
import * as React from "react";
import { useQueryClient } from "react-query";
import { useDispatch } from "react-redux";
import { Action, Dispatch } from "redux";
import { Actions, loadAllUsers } from "store/actions";
import {
    ClientIntakeEmailResponse,
    ClientIntakeSendResult,
    readSendOperationId,
} from "store/actions/email";
import { makeRequestAction } from "store/actions/types";
import { usePreferences } from "store/selectors/hooks";
import { RequestDescription } from "types/request-description";
import { handleEmailSent } from "util/email-sent";
import { handleEvent } from "util/user-events";
import { RequestState } from "../../../../constants/loading";
import { cancelSendConfirmation } from "./confirm-dialog";

const sendingLabel = "Sending...";

const SendingPanel: React.FC<{
    eventId: InternalId;
    operationId: AsyncOperationId | null;
}> = ({ eventId, operationId }) => {
    const dispatch = useDispatch<Dispatch<Action>>();
    const queryClient = useQueryClient();
    const operation = useAsyncOperation(
        operationId,
        standardAsyncOperationPollIntervalMs,
    );
    const handle = operation.handle;
    const status = handle?.status ?? null;
    useRefreshFeedWhenSendSettles(operationId, status, queryClient);

    const handleCancel = async () => {
        if (await cancelSendConfirmation()) {
            operation.cancel();
        }
    };

    React.useEffect(() => {
        if (!handle || !isAsyncOperationTerminal(handle.status)) {
            return;
        }
        handleEmailSent(queryClient);
        if (handle.status === AsyncOperationStatus.Succeeded) {
            const result = handle.result as ClientIntakeSendResult;
            dispatch(
                makeRequestAction(
                    result.anyAccepted
                        ? Actions.EMAIL_FORM_SUCCESS
                        : Actions.EMAIL_FORM_ERROR,
                    eventId,
                    { deliveryResults: result.deliveryResults },
                ),
            );
            return;
        }
        if (handle.status === AsyncOperationStatus.Canceled) {
            dispatch(
                makeRequestAction(Actions.EMAIL_FORM_ERROR, eventId, {
                    deliveryResults: [],
                    canceled: true,
                }),
            );
            return;
        }
        dispatch(
            makeRequestAction(Actions.EMAIL_FORM_ERROR, eventId, {
                deliveryResults: [],
                errorMessage: handle.errorMessage,
            }),
        );
    }, [status]); // eslint-disable-line react-hooks/exhaustive-deps

    if (!operation.handle) {
        return (
            <div className="flex items-center space-x-2">
                <Spinner />
                <span className="text-sm text-gray-700">{sendingLabel}</span>
            </div>
        );
    }

    return (
        <AsyncOperationProgress
            handle={operation.handle}
            percentComplete={operation.percentComplete}
            pendingLabel={sendingLabel}
            cancelLabel="Stop sending"
            cancelingLabel="Stopping..."
            onCancel={handleCancel}
            isCanceling={operation.isCanceling}
        />
    );
};

export const BottomPanel = (props: {
    eventId: InternalId;
    requestDescription: RequestDescription;
    dismiss: () => void;
    sendMessage: () => void;
    newMessage: () => void;
    sendEnabled: boolean;
}): JSX.Element => {
    const { requestDescription } = props;
    const preferences = usePreferences();
    const dispatch = useDispatch<Dispatch<Action>>();

    const disablePrompt = React.useCallback(async () => {
        await trpcClient.userPreference.setBooleanPreference.mutate({
            field: "promptSendAnotherEmail",
            value: false,
        });
        handleEvent("preference-change", {
            field: "promptSendAnotherEmail",
            value: false,
        });
        await loadAllUsers(dispatch);
    }, [dispatch]);

    React.useEffect(() => {
        (async () => {
            if (
                requestDescription.status === RequestState.DONE ||
                requestDescription.status === RequestState.ERROR
            ) {
                const response =
                    typeof requestDescription.data === "object"
                        ? (requestDescription.data as ClientIntakeEmailResponse)
                        : null;

                if (response?.canceled) {
                    props.dismiss();
                    return;
                }

                let accepted: string[] = [];
                let rejected: string[] = [];
                let authError = false;
                let providerErrorCode: string | null = null;
                let anyDeliveryFullyRejected = false;

                if (response && Array.isArray(response.deliveryResults)) {
                    response.deliveryResults.forEach(dr => {
                        const acceptedForResult = Array.isArray(dr.accepted)
                            ? dr.accepted
                            : [];
                        const rejectedForResult = Array.isArray(dr.rejected)
                            ? dr.rejected
                            : [];
                        accepted = accepted.concat(acceptedForResult);
                        rejected = rejected.concat(rejectedForResult);

                        if (
                            dr.code === "MailboxNotEnabledForRESTAPI" ||
                            dr.code === gmailMailboxUnavailableCode ||
                            dr.code === outlookTemporarilyUnavailableCode
                        ) {
                            providerErrorCode = dr.code;
                        }

                        if (dr.code === "EAUTH") {
                            authError = true;
                        }

                        if (
                            acceptedForResult.length === 0 &&
                            rejectedForResult.length > 0
                        ) {
                            anyDeliveryFullyRejected = true;
                        }
                    });
                }
                rejected = Array.from(new Set(rejected));

                const outcome = describeDeliveryOutcome({
                    succeeded:
                        accepted.length > 0
                            ? !anyDeliveryFullyRejected
                            : requestDescription.status === RequestState.DONE &&
                              rejected.length === 0,
                    rejected,
                    authError,
                    providerErrorCode,
                });

                switch (outcome.kind) {
                    case "auth-error":
                        await fancyAlert(authErrorTitle, authErrorBody);
                        break;
                    case "mailbox-unavailable":
                        await fancyAlert(mailboxUnavailableTitle, mailboxUnavailableBody);
                        break;
                    case "gmail-mailbox-unavailable":
                        await fancyAlert(
                            gmailMailboxUnavailableTitle,
                            gmailMailboxUnavailableBody,
                        );
                        break;
                    case "outlook-temporarily-unavailable":
                        await fancyAlert(
                            outlookTemporarilyUnavailableTitle,
                            outlookTemporarilyUnavailableRecipientsBody(outcome.rejected),
                        );
                        break;
                    case "partial":
                        await fancyAlert(
                            partialSendTitle,
                            partialSendBody(outcome.rejected),
                        );
                        props.dismiss();
                        break;
                    case "all-rejected": {
                        const sendAnother = await fancyConfirm(
                            allRejectedTitle,
                            `${allRejectedBody(
                                outcome.rejected,
                            )}\n\n${allRejectedRetryPrompt}`,
                            "Yes",
                            "No",
                        );

                        if (sendAnother) {
                            props.newMessage();
                        } else {
                            props.dismiss();
                        }
                        break;
                    }
                    case "success":
                        if (!preferences.promptSendAnotherEmail) {
                            handleEvent("email-send-another-prompt", {
                                response: "skipped-disabled",
                            });
                            props.dismiss();
                        } else {
                            const result = await fancyChoice({
                                title: "Mail successfully sent.",
                                prompt: "Would you like to send another email?",
                                actions: [
                                    { label: "Yes", value: "yes", style: "primary" },
                                    { label: "No", value: "no", style: "secondary" },
                                    {
                                        label: "No, and never ask me again",
                                        value: "never",
                                        style: "subtle",
                                    },
                                ],
                            });
                            handleEvent("email-send-another-prompt", {
                                response: result,
                            });
                            if (result === "yes") {
                                props.newMessage();
                            } else {
                                if (result === "never") {
                                    await disablePrompt();
                                }
                                props.dismiss();
                            }
                        }
                        break;
                    case "unknown-error":
                        await fancyAlert(
                            unknownErrorTitle,
                            response?.errorMessage ?? unknownErrorBody,
                        );
                        break;
                    default: {
                        const exhaustiveCheck: never = outcome;
                        throw new RangeError(
                            `Expected ${exhaustiveCheck} to be matched, but wasn't.`,
                        );
                    }
                }
            }
        })();
    }, [requestDescription.status]); // eslint-disable-line react-hooks/exhaustive-deps
    if (requestDescription.status === RequestState.NOTSTARTED) {
        return (
            <div className="flex items-center justify-end space-x-3">
                <div>
                    <SecondaryButton onClick={props.dismiss}>Discard</SecondaryButton>
                </div>
                <div>
                    <PrimaryButton
                        onClick={props.sendMessage}
                        disabled={!props.sendEnabled}>
                        Send
                    </PrimaryButton>
                </div>
            </div>
        );
    }
    if (requestDescription.status === RequestState.REQUESTED) {
        return (
            <SendingPanel
                eventId={props.eventId}
                operationId={readSendOperationId(requestDescription.data)}
            />
        );
    }
    if (
        requestDescription.status === RequestState.DONE ||
        requestDescription.status === RequestState.ERROR
    ) {
        return null;
    }
    const exhaustiveCheck: never = requestDescription.status;
    throw new RangeError(`Expected ${exhaustiveCheck} to be matched, but wasn't.`);
};
